import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import ReportTable from "../../common/ReportTable/ReportTable";
import { useLoader } from "../../../context/LoaderContext";
import { ESourceAccessGate, useESourceAccess } from "./esourceAccess";
import {
  createInstance,
  fetchInstances,
  fetchTemplates,
} from "./esourceService";
import "./ESourcePages.scss";

const FIELD_OPTIONS = [
  { key: "Title", label: "Title" },
  { key: "TemplateName", label: "Form" },
  { key: "TemplateCode", label: "Code" },
  { key: "VersionNo", label: "Version" },
  { key: "Status", label: "Status" },
  { key: "ModifiedAt", label: "Modified" },
];

const DEFAULT_COLUMNS = [
  "Title",
  "TemplateName",
  "TemplateCode",
  "VersionNo",
  "Status",
  "ModifiedAt",
];

const ESourceInstanceList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, canView, canFill } = useESourceAccess();
  const { showLoader, hideLoader } = useLoader();
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [templateId, setTemplateId] = useState(
    searchParams.get("templateId") || "",
  );

  const load = useCallback(async () => {
    setListLoading(true);
    showLoader();
    try {
      const [inst, tmpl] = await Promise.all([
        fetchInstances(templateId ? { FormTemplateId: templateId } : {}),
        fetchTemplates(),
      ]);
      if (inst.success) setRows(inst.rows || []);
      else setRows([]);
      if (tmpl.success) setTemplates(tmpl.rows || []);
    } finally {
      hideLoader();
      setListLoading(false);
    }
  }, [templateId, showLoader, hideLoader]);

  useEffect(() => {
    if (!loading && (canView || canFill)) load();
  }, [loading, canView, canFill, load]);

  const rowActions = useMemo(
    () => [
      {
        label: "Open",
        icon: "fas fa-folder-open",
        onClick: (row) => navigate(`/esource/instances/${row.FormInstanceId}`),
      },
    ],
    [navigate],
  );

  const renderCell = useCallback(
    (row, field) => {
      if (field === "Title") {
        return (
          <button
            type="button"
            className="report-table__cell-link"
            onClick={() => navigate(`/esource/instances/${row.FormInstanceId}`)}
          >
            {row.Title || "—"}
          </button>
        );
      }
      if (field === "Status") {
        const status = String(row.Status || "");
        return (
          <span
            className={`esource-status esource-status--${status
              .toLowerCase()
              .replace(/\s+/g, "")}`}
          >
            {status || "—"}
          </span>
        );
      }
      if (field === "ModifiedAt") {
        const v = row.ModifiedAt || row.CreatedAt;
        return v ? new Date(v).toLocaleString() : "—";
      }
      if (field === "VersionNo") {
        return row.VersionNo != null ? `v${row.VersionNo}` : "—";
      }
      const value = row[field];
      return value == null || value === "" ? "—" : String(value);
    },
    [navigate],
  );

  const onCreate = useCallback(async () => {
    if (!templateId) return;
    showLoader();
    try {
      const res = await createInstance({
        FormTemplateId: templateId,
        Title: "New form record",
      });
      if (res.success && res.formInstanceId) {
        navigate(`/esource/instances/${res.formInstanceId}`);
      }
    } finally {
      hideLoader();
    }
  }, [templateId, showLoader, hideLoader, navigate]);

  const breadcrumbActions = useMemo(() => {
    const actions = [
      {
        icon: "fas fa-sync-alt",
        label: "Refresh",
        onClick: load,
      },
    ];
    if (canFill) {
      actions.push({
        icon: "fas fa-plus",
        label: "New form record",
        onClick: onCreate,
        disabled: !templateId,
      });
    }
    return actions;
  }, [canFill, load, onCreate, templateId]);

  return (
    <ESourceAccessGate loading={loading} allow={canView || canFill}>
      <div className="esource-page esource-list-page">
        <PageBreadcrumb
          items={[
            { label: "Home", path: "/notification-center" },
            { label: "e-Source", path: "/esource/templates" },
            { label: "Form records" },
          ]}
          actions={breadcrumbActions}
          toolbarExtras={
            <select
              className="form-select form-select-sm esource-toolbar-select"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              aria-label="Filter by form"
            >
              <option value="">All forms</option>
              {templates.map((t) => (
                <option key={t.FormTemplateId} value={t.FormTemplateId}>
                  {t.Code} — {t.Name}
                  {t.PublishedVersionNo
                    ? ` (v${t.PublishedVersionNo})`
                    : " (not published)"}
                </option>
              ))}
            </select>
          }
        />
        <div className="esource-report-wrap">
          <ReportTable
            rows={rows}
            loading={listLoading}
            fieldOptions={FIELD_OPTIONS}
            defaultColumns={DEFAULT_COLUMNS}
            getFieldLabel={(field) =>
              FIELD_OPTIONS.find((x) => x.key === field)?.label || field
            }
            renderCell={renderCell}
            rowActions={rowActions}
            persistLayoutKey="esource-instances-v1"
            emptyMessage="No form records yet."
            emptyDescription="Choose a published form, then use New form record to start data capture."
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>
      </div>
    </ESourceAccessGate>
  );
};

export default ESourceInstanceList;
